/**
 * Utility functions for converting between decimal hours and hours/minutes format
 */

import { formatDisplayDateTime } from '../../utils/formatDisplayDate';

export interface HoursMinutes {
    hours: number;
    minutes: number;
}

/**
 * Converts decimal hours to hours and minutes
 * @param decimalHours - Time in decimal hours (e.g., 1.5 = 1 hour 30 minutes)
 * @returns Object with hours and minutes (both whole numbers, minutes 0-60)
 */
export function decimalHoursToHoursMinutes(decimalHours: number): HoursMinutes {
    if (isNaN(decimalHours) || decimalHours < 0) {
        return { hours: 0, minutes: 0 };
    }
    
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return { hours, minutes };
}

/**
 * Converts hours and minutes to decimal hours
 * @param hours - Whole number of hours (0-60)
 * @param minutes - Whole number of minutes (0-60)
 * @returns Decimal hours (e.g., 1 hour 30 minutes = 1.5)
 */
export function hoursMinutesToDecimalHours(hours: number, minutes: number): number {
    const validHours = Math.max(0, Math.min(60, Math.floor(hours || 0)));
    const validMinutes = Math.max(0, Math.min(60, Math.floor(minutes || 0)));
    
    return validHours + (validMinutes / 60);
}

/** Minimum duration for a lesson activity (30 minutes). */
export const MIN_LESSON_ACTIVITY_DURATION_HOURS = 0.5;

export function isBelowMinLessonActivityDuration(decimalHours: number): boolean {
    return !Number.isFinite(decimalHours) || decimalHours < MIN_LESSON_ACTIVITY_DURATION_HOURS;
}

/** Keep activity minutes in 0–60, wrapping past either end (for step-15 spinners). */
export function normalizeWrappedMinutesInput(raw: string): string | null {
    if (raw === '') return '';
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return null;
    if (n > 60) return '0';
    if (n < 0) return '60';
    return String(n);
}

/**
 * Formats decimal hours as a human-readable string
 * @param decimalHours - Time in decimal hours
 * @returns Formatted string like "1 hour 30 minutes" or "45 minutes"
 */
export function formatTimeDisplay(decimalHours: number): string {
    if (isNaN(decimalHours) || decimalHours <= 0) {
        return "0 minutes";
    }
    
    const { hours, minutes } = decimalHoursToHoursMinutes(decimalHours);
    
    const parts: string[] = [];
    
    if (hours > 0) {
        parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }
    
    if (minutes > 0) {
        parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }
    
    if (parts.length === 0) {
        return "0 minutes";
    }
    
    return parts.join(' ');
}

export function formatIsoDateTimeShort(iso: string | null | undefined): string | null {
    if (iso == null || iso === '') return null;
    const formatted = formatDisplayDateTime(iso);
    return formatted || null;
}



